import { Kartutsnitt } from '@/domain/kart/Kartutsnitt';
import {
    padVegsystemreferanse,
    validateVegsystemreferanse,
    Vegsystemreferanse,
} from '@/domain/omrader';
import { Vegnettlenke, VegnettlenkeReferanse } from '@/domain/vegnett/Vegnettlenke';
import { Vegobjekt, vegobjektVersionsComparator } from '@/domain/vegobjekter/Vegobjekt';
import { fetchVegSystemReferanse } from '@/middleware/fetchMisc';
import { matchesLinkQuery, parseLinkQuery } from '@/middleware/fetchVegnett';
import { Integration } from '@/middleware/integration';
import {
    stateSearchTimestamp,
    stateValgt,
    stateVegsystemreferanser,
    stateVegsystemreferanseSok,
} from '@/selectors/selectors';
import {
    MapState,
    PreferencesState,
    PREFERENCES_STORAGE_KEY,
    RootState,
    UserState,
    ValgtState,
    VegkartError,
    VegsystemreferanseLookup,
} from '@/state';
import { hashToState } from '@/state/hash/HashToState';
import { SplashState } from '@/state/SplashState';
import { loadAuthConfig } from '@/utils/authConfig';
import {
    LAST_LOGIN_KEY,
    storageHasToken,
    SVVTOKENS_KEY,
} from '@/utils/AuthenticationUtils';
import { ColorHandler, defaultColor, resolveColor } from '@/utils/ColorHelper';
import { handleOAuth2Callback, hasOAuthCallback, isOAuthPopup } from '@/utils/pkce';
import { getHash } from '@/utils/Utils';
import { createVegrefParser } from '@/utils/VegrefParser';
import { runtimeEnvironment } from '@/config';
import { Data } from './data';
import isEqual from 'lodash-es/isEqual';

type StoredTokens = {
    access_token?: string;
    id_token?: string;
};

export async function bootstrap(
    integration: Integration,
    shellState: RootState,
    data: Data
): Promise<RootState> {
    integration.setData(data);
    integration.setColorHandler(new ColorHandler([]));

    await loadAuthConfig(integration.config.restapi_url);

    // Mobile redirect flow: oauth-callback.html stored tokens in localStorage; move them to sessionStorage
    const mobileTokens = localStorage.getItem(SVVTOKENS_KEY);
    if (mobileTokens && !sessionStorage.getItem(SVVTOKENS_KEY)) {
        sessionStorage.setItem(SVVTOKENS_KEY, mobileTokens);
        const mobileLastLogin = localStorage.getItem(LAST_LOGIN_KEY);
        if (mobileLastLogin) sessionStorage.setItem(LAST_LOGIN_KEY, mobileLastLogin);
        localStorage.removeItem(SVVTOKENS_KEY);
        localStorage.removeItem(LAST_LOGIN_KEY);
    }

    const tokens = hasOAuthCallback()
        ? await handleOAuth2Callback()
        : (JSON.parse(
              sessionStorage.getItem(SVVTOKENS_KEY) ?? 'null'
          ) as StoredTokens | null);

    if (tokens?.access_token) {
        if (!isOAuthPopup() || !hasOAuthCallback()) {
            integration.server.setSVVToken(tokens.access_token);
        }
    } else if (hasOAuthCallback()) {
        console.error('Token exchange failed');
    }

    const storedPreferences = JSON.parse(
        localStorage.getItem(PREFERENCES_STORAGE_KEY) ?? 'null'
    );

    const preferences = new PreferencesState()
        .override({
            showDeveloperSettings: ['local', 'stm', 'dev'].includes(runtimeEnvironment()),
        })
        .override(storedPreferences);

    const stateFromUrl: RootState = hashToState(
        integration,
        shellState.withPreferences(preferences),
        getHash(window.location.hash)
    );
    return bootstrapState(integration, stateFromUrl);
}

/*
 * Show main splash first time a user opens Vegkart
 */
function bootstrapSplash(splashState: SplashState) {
    const showOnStartup = localStorage.getItem('ShowSplashOnStartup') !== 'false';

    if (
        showOnStartup === null &&
        (splashState === null || splashState === SplashState.NONE)
    ) {
        localStorage.setItem('ShowSplashOnStartup', 'false');
    }
    if (showOnStartup === null && splashState === null) {
        return SplashState.MAIN;
    }
    return splashState;
}

async function bootstrapState(
    integration: Integration,
    stateFromUrl: RootState
): Promise<RootState> {
    // Starts all calls asynchronously
    const selectionPromise: Promise<ValgtState> = bootstrapMapSelection(
        integration,
        stateFromUrl
    );
    const vsrSokPromise: Promise<VegsystemreferanseLookup | null> = bootstrapPopups(
        integration,
        stateFromUrl
    );
    const kartutsnittPromise: Promise<Kartutsnitt | null> = selectionPromise.then(
        mapSelection => bootstrapZoomToExtent(stateFromUrl.withValgt(mapSelection))
    );
    const vegsystemReferanser = bootstrapVegsystemreferanser(integration, stateFromUrl);
    const userState = bootstrapUserState();
    // Awaits calls to enable concurrent send-off.
    return stateFromUrl
        .withOmrader(
            stateFromUrl.omraderState.withVegsystemreferanser(await vegsystemReferanser)
        )
        .withMap(
            stateFromUrl.mapState.withZoomToExtent(
                (await kartutsnittPromise) as Kartutsnitt
            )
        )
        .withValgt(await selectionPromise)
        .withOverlay(
            stateFromUrl.overlayState.withVegsystemreferanseLookup(
                (await vsrSokPromise) as VegsystemreferanseLookup
            )
        )
        .withSplash(bootstrapSplash(stateFromUrl.splashState))
        .withUser(userState);
}

function bootstrapUserState() {
    if (!storageHasToken()) {
        return new UserState();
    }

    const tokens = JSON.parse(
        sessionStorage.getItem(SVVTOKENS_KEY) ?? 'null'
    ) as StoredTokens | null;
    if (!tokens?.id_token) {
        throw new Error('Stored authentication token is missing an id_token');
    }
    return UserState.fromIdToken(tokens.id_token);
}

async function bootstrapVegsystemreferanser(
    integration: Integration,
    stateFromUrl: RootState
): Promise<Vegsystemreferanse[]> {
    const urlState = stateVegsystemreferanser(stateFromUrl);
    const validVegsystemreferanser = urlState.filter(validateVegsystemreferanse);

    if (!validVegsystemreferanser.length) return urlState;

    const promises = urlState.map(vegsystemreferanse =>
        fetchVegSystemReferanse(vegsystemreferanse, integration)
    );

    return Promise.all(promises);
}

function createSparseVegobjektHydrator(
    integration: Integration,
    zoom: number,
    timestamp: Date | null
): (sparseVegobjekt: Vegobjekt) => Promise<Vegobjekt | null> {
    const cache = new Map<string, Promise<Vegobjekt | null>>();

    return sparseVegobjekt => {
        if (!sparseVegobjekt) {
            return Promise.resolve(null);
        }

        const cacheKey = `${sparseVegobjekt.id}:${sparseVegobjekt.metadata?.type?.id ?? ''}`;
        let request = cache.get(cacheKey);
        if (!request) {
            request = bootstrapSelectedVegobjekt(
                integration,
                zoom,
                sparseVegobjekt,
                timestamp
            );
            cache.set(cacheKey, request);
        }
        return request;
    };
}

async function bootstrapSelectedVegobjekter(
    sparseVegobjekter: Vegobjekt[],
    hydrateVegobjekt: (sparseVegobjekt: Vegobjekt) => Promise<Vegobjekt | null>
): Promise<Vegobjekt[]> {
    const completed = await Promise.all((sparseVegobjekter || []).map(hydrateVegobjekt));
    return completed.filter(isDefined);
}

export async function bootstrapSelectedVegobjekt(
    integration: Integration,
    zoom: number,
    sparseVegobjekt: Vegobjekt,
    timestamp: Date | null
): Promise<Vegobjekt | null> {
    if (!sparseVegobjekt) {
        return null;
    }

    if (sparseVegobjekt.metadata?.type.id != null) {
        return integration.server
            .getVegobjektVersionsMinimal(
                sparseVegobjekt.id,
                sparseVegobjekt.metadata.type.id
            )
            .then((vegobjekterFromServer: Vegobjekt[]) => {
                const version = timestamp
                    ? vegobjekterFromServer
                          .filter(
                              version => new Date(version.metadata.startdato) <= timestamp
                          )
                          .sort(vegobjektVersionsComparator)
                          .pop()?.metadata.version
                    : undefined;
                return integration.server.getVegobjekt(
                    sparseVegobjekt.id,
                    undefined,
                    sparseVegobjekt.metadata.type.id,
                    version
                );
            })
            .catch(() => {
                return null;
            });
    }

    return integration.server
        .getVegobjekt(sparseVegobjekt.id, undefined)
        .then(vegobjektFromServer => {
            return vegobjektFromServer;
        })
        .catch(() => {
            return null;
        });
}

export async function bootstrapSelectedVeglenke(
    integration: Integration,
    sparseVeglenke: VegnettlenkeReferanse | null
): Promise<Vegnettlenke[] | null> {
    if (!sparseVeglenke) {
        return null;
    }

    const veglenkesekvens: Vegnettlenke[] = await integration.server.getVeglenkesekvens(
        sparseVeglenke.veglenkesekvensid,
        true
    );

    const matchingSegments = veglenkesekvens.filter(
        segment =>
            segment.veglenkenummer === sparseVeglenke.veglenkenummer &&
            (sparseVeglenke.segmentnummer == null ||
                segment.segmentnummer === sparseVeglenke.segmentnummer)
    );
    return matchingSegments.reduce<Vegnettlenke[]>(
        (segmentArray, currentObject) =>
            !segmentArray.length ||
            currentObject.segmentnummer > segmentArray[0].segmentnummer
                ? [currentObject]
                : segmentArray,
        []
    );
}

function isDefined<T>(value: T | null | undefined): value is T {
    return value != null;
}

async function bootstrapMapSelection(
    integration: Integration,
    stateFromUrl: RootState
): Promise<ValgtState> {
    const urlState = stateValgt(stateFromUrl);
    const sparseVegobjekter = urlState.vegobjekter;
    const sparseClusterObjekter = urlState.clusterObjekter;
    const sparseVeglenke = urlState.singleVegnettlenkeReferanse();
    const sparseLinkQueries: string[] = Object.keys(urlState.linkQueries);
    const sparseNodes = urlState.nodes.map(n => n.id);
    const timestamp = stateSearchTimestamp(stateFromUrl) || new Date();
    const hydrateVegobjekt = createSparseVegobjektHydrator(
        integration,
        stateFromUrl.mapState.zoom,
        timestamp
    );

    const [
        vegobjekter,
        clusterObjekter,
        veglenkesegment,
        linkQueryResultSet,
        nodeResultSet,
    ] = await Promise.all([
        bootstrapSelectedVegobjekter(sparseVegobjekter, hydrateVegobjekt),
        bootstrapSelectedVegobjekter(sparseClusterObjekter, hydrateVegobjekt),
        bootstrapSelectedVeglenke(integration, sparseVeglenke),
        Promise.all(
            sparseLinkQueries.map(async queryString => {
                const query = parseLinkQuery(queryString);
                let links = await integration.server.getVeglenkesekvens(query.id);
                links = links.filter(s => matchesLinkQuery(query, s));
                return { queryString, links };
            })
        ),
        Promise.all(sparseNodes.map(async id => integration.server.getNode(id))),
    ]);

    function resolveSelectedColor(): string {
        const colorSource = vegobjekter[0] || clusterObjekter[0];
        if (!colorSource) return defaultColor;

        const matchingType = stateFromUrl.vegobjekterState.vegobjekttypeStates.find(
            x => x.typeId === colorSource.metadata?.type.id
        );
        return matchingType?.color ? resolveColor(matchingType.color) : defaultColor;
    }

    let state = ValgtState.emptySelection();
    if (vegobjekter.length > 0) state = state.withVegobjekter(vegobjekter);
    if (clusterObjekter.length > 0) state = state.withClusterObjekter(clusterObjekter);
    if (veglenkesegment) state = state.withVegnettlenker(veglenkesegment);
    if (vegobjekter.length > 0 || clusterObjekter.length > 0 || veglenkesegment) {
        state = state.withColor(resolveSelectedColor());
    }
    if (nodeResultSet.length > 0) state = state.withNodes(nodeResultSet);
    if (linkQueryResultSet.length > 0) {
        const linkQueries = linkQueryResultSet.reduce(
            (queries, { queryString, links }) => ({
                ...queries,
                [queryString]: links,
            }),
            {}
        );
        state = state.withLinkQueries(linkQueries);
    }
    return state;
}

async function bootstrapPopups(
    integration: Integration,
    stateFromUrl: RootState
): Promise<VegsystemreferanseLookup | null> {
    const sparseVegSysRefSok: VegsystemreferanseLookup | null =
        stateVegsystemreferanseSok(stateFromUrl);
    const ref = sparseVegSysRefSok?.vegsystemreferanse;

    if (ref?.kortform) {
        const parser = createVegrefParser(integration.data.datakatalog.getTypeById(915));
        const parsedReference = parser(ref.kortform);
        if (!parsedReference) {
            throw new Error(`Kunne ikke tolke vegsystemreferanse ${ref.kortform}`);
        }
        const parsedWithKommune = parsedReference
            .withKortform(ref.kortform)
            .override({ kommune: ref.kommune });
        const center = await integration.server.getVeg(
            padVegsystemreferanse(parsedWithKommune)
        );
        return new VegsystemreferanseLookup(
            0,
            0,
            parsedWithKommune.withSenterpunkt(center),
            undefined,
            center,
            0,
            parsedWithKommune.kommune
        );
    }
    const coordsToVegsysref: VegsystemreferanseLookup | VegkartError | null =
        sparseVegSysRefSok
            ? await integration.server.getVSRFromCoordinate(
                  [sparseVegSysRefSok.east, sparseVegSysRefSok.north],
                  stateSearchTimestamp(stateFromUrl) ?? undefined
              )
            : null;

    return coordsToVegsysref instanceof VegsystemreferanseLookup
        ? coordsToVegsysref
        : null;
}

async function bootstrapZoomToExtent(
    stateWithSelection: RootState
): Promise<Kartutsnitt | null> {
    const sparseMapState: MapState = stateWithSelection.mapState;
    const selection: ValgtState = stateWithSelection.valgtState;
    const defaultMapState = new MapState();
    // Direct link with modified map state -> do nothing.
    if (!isEqual(sparseMapState, defaultMapState)) {
        return null;
    }
    // Direct link with selection -> return kartutsnitt.
    if (!selection.isEmpty()) {
        return selection.getKartutsnitt();
    }
    // Safety net. Note that the areas from url might set their own zoomToExtent in components. This is fine.
    return null;
}
